/**
 * 내부 도메인 allowlist 기반 외부 URL 필터.
 *
 * HyperDX(OTel) fetch/xhr/resource instrumentation은 기본적으로 페이지에서 발생하는
 * 모든 네트워크 요청을 span으로 수집한다. 그 결과 구글 애널리틱스/광고(doubleclick,
 * googleads, adtrafficquality), 제타(ingestion.zetta.so), 페이스북, 채널톡 등
 * "우리 서비스와 무관한 제3자 요청"이 전체 span의 다수(web-app 기준 ~88%, quiz-app ~61%)를
 * 차지한다.
 *
 * `ignoreUrls`는 "매칭되면 기록하지 않는다"는 denylist 시맨틱이다. 제3자 도메인은 계속
 * 늘어나므로 denylist로 일일이 나열하면 새 제3자가 추가될 때마다 다시 샌다. 대신 여기서는
 * "내부 도메인이 아닌 모든 절대 URL"을 매칭시키는 **내부 allowlist** 전략을 쓴다.
 *
 * 동작:
 * - `https://live-quiz-api.zep.us/...` → 내부 → 기록 유지
 * - `https://www.google-analytics.com/...` → 외부 → drop
 * - `/api/ping` 같은 동일 출처 상대경로 → instrumentation이 절대 URL로 변환하므로 내부로 유지
 */

/** 내부로 간주할 base 도메인. 서브도메인은 자동 포함된다 (예: `live-quiz-api.zep.us`). */
export const DEFAULT_INTERNAL_HOSTS = [
  'zep.us',
  'zep.works',
  'zep.site',
] as const;

const escapeForRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * "호스트가 내부 도메인이 아닌 모든 http(s) URL"에 매칭되는 정규식을 만든다.
 * `ignoreUrls`에 그대로 넣으면 외부 네트워크 span만 drop된다.
 *
 * 정규식 해설:
 * - `^https?://`              절대 http(s) URL만 대상 (상대경로/data:/blob: 는 미매칭 → 유지)
 * - `(?! ... )`               authority(호스트[:포트]) 안에 내부 도메인이 있으면 매칭 실패 → 유지
 * - `(?:[^/?#]*\.)?`          서브도메인 경계: 내부 도메인 앞에는 끝이 `.`이어야 함
 *                             → `notzep.us` 같은 가짜 접미사를 외부로 올바르게 분류
 * - `(?:[:/?#]|$)`            내부 도메인 뒤는 포트/경로/쿼리/프래그먼트/끝이어야 함
 *                             → 쿼리스트링에 박힌 `?url=https://zep.us...`(doubleclick 등)는 무시
 */
export const buildExternalIgnoreUrls = (
  internalHosts: readonly string[] = DEFAULT_INTERNAL_HOSTS,
): RegExp[] => {
  const hostsAlternation = internalHosts.map(escapeForRegExp).join('|');
  return [
    new RegExp(
      `^https?://(?!(?:[^/?#]*\\.)?(?:${hostsAlternation})(?:[:/?#]|$))`,
      'i',
    ),
  ];
};

/**
 * 앱 config에서 `ignoreUrls`를 지정하지 않은 경우 HyperDX init에 적용되는 기본값.
 * 내부(zep) 도메인만 남기고 외부 제3자 네트워크 span을 전부 drop한다.
 */
export const DEFAULT_EXTERNAL_IGNORE_URLS: RegExp[] = buildExternalIgnoreUrls();
